import React, { useEffect, createContext, useState, useContext, } from 'react';
import { DB, } from '@tonic/central-specialties-utils';
import { RoutingLibContext } from './RoutingLibProvider';
export const UserContext = createContext({
    user: null,
    pending: true,
    sync: async () => null,
    login: (e, p, r) => DB.auth.login(e, p, r),
    logout: DB.auth.logout,
    sectionsAvailable: [],
    isAdmin: false,
    rememberUser: false,
});
export const UserProvider = ({ localStorageManager, unguardedPaths = ['/auth/login', '/auth/forgot-password'], children, }) => {
    const [user, setUser] = useState(null);
    const [pending, setPending] = useState(true);
    const [sectionsAvailable, setSectionsAvailable] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const { useNavigate, useLocation } = useContext(RoutingLibContext);
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [rememberUser, setRememberUser] = useState(false);
    // Determine which sections the user should be able to see
    useEffect(() => {
        const loadAvailableSections = async () => {
            if (user) {
                setSectionsAvailable(await DB.auth.getAuthorizedFeatureCategories(user));
            }
            else {
                setSectionsAvailable([]);
            }
        };
        loadAvailableSections();
    }, [user]);
    // Determine whether the user is an admin of any kind
    useEffect(() => {
        const determineIsAdmin = async () => {
            if (user && !!user?.auth?.token) {
                const decodedToken = await DB.auth.getDecodedToken(user.auth.token);
                setIsAdmin(!!decodedToken &&
                    ((Array.isArray(decodedToken.roles) &&
                        decodedToken.roles.some((role) => role.includes('Admin') || role === 'PartsTeam')) ||
                        (typeof decodedToken.roles === 'string' &&
                            (decodedToken.roles.includes('Admin') ||
                                decodedToken.roles === 'PartsTeam'))));
            }
            else {
                setIsAdmin(false);
            }
        };
        determineIsAdmin();
    }, [user]);
    useEffect(() => {
        const initialize = async () => {
            if (!DB.isInitialized) {
                const onAuthStateChanged = (token) => {
                    if (token === null) {
                        setUser(null);
                    }
                };
                await DB.init(localStorageManager, onAuthStateChanged);
                const cachedUser = await DB.auth.getUser();
                if (cachedUser) {
                    setUser(cachedUser);
                }
                setPending(false);
            }
        };
        initialize();
    }, [localStorageManager]);
    // Redirect guard, ensuring only auth paths are accessible without authentication
    useEffect(() => {
        if (pending) {
            return;
        }
        if (!user && !unguardedPaths.some((path) => pathname.includes(path))) {
            navigate('/auth/login');
        }
        else if (user &&
            user.auth.needsToChangePassword &&
            pathname !== '/auth/create-password') {
            navigate('/auth/create-password');
        }
    }, [user, pending, navigate, pathname, unguardedPaths]);
    const sync = async () => {
        const newUserData = await DB.auth.syncUser();
        setUser(newUserData);
        return newUserData;
    };
    const login = async (email, password, rememberMe = false, onError = (e) => console.error('login failed: ', e)) => {
        try {
            setPending(true);
            const fetchedUser = await DB.auth.login(email, password, rememberMe);
            setUser(fetchedUser);
            return fetchedUser;
        }
        catch (e) {
            console.error('Login failed: ', e);
            onError && onError(e);
            throw e;
        }
        finally {
            setRememberUser(rememberMe);
            setPending(false);
        }
    };
    const logout = async () => {
        await DB.auth.logout();
        setUser(null);
    };
    return (React.createElement(UserContext.Provider, { value: { user, pending, sync, login, logout, sectionsAvailable, isAdmin, rememberUser } }, children));
};
